import { useEffect, useState } from 'react';
import API from '../../../../context/API';
import { notification } from '../../../../commonComponents/Notification/Notification';
import { CustomizationData, RelatedItem } from '../types';

export const useCustomizationSideBox = (
  customizationData: CustomizationData,
  handleInputChange: (field: string, value: any) => void,
  handleSubmit: (submissionType: string, data: any) => void | Promise<void>,
  setCustomizationData: (data: CustomizationData | null | ((prev: CustomizationData | null) => CustomizationData | null)) => void,
  selectedItem: number | null,
  isEditOption: boolean,
  autoEditOptions: boolean,
) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [editOptionsMode, setEditOptionsMode] = useState(isEditOption || false);
  const [isOption, setIsOption] = useState(true);
  const [addToItemsMode, setAddToItemsMode] = useState(false);
  const [customizationRulesMode, setCustomizationRulesMode] = useState(false);
  const [soldOut, setSoldOut] = useState(customizationData?.sold_out || false);

  useEffect(() => {
    if (autoEditOptions && customizationData) {
      setEditOptionsMode(true);
    }
  }, [autoEditOptions, customizationData]);

  const handleEditNameSave = async () => {
    if (!customizationData) return;
    setIsSavingName(true);
    try {
      await handleSubmit('name', customizationData);
      setIsEditModalOpen(false);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleBackClick = () => {
    setEditOptionsMode(false);
    setAddToItemsMode(false);
    setCustomizationRulesMode(false);
    setIsOption(false);
  };

  const handleStockToggle = async (customizationId: number, soldOutValue: boolean) => {
    try {
      const response = await API.get(`/customizations/stock-toggle?customization=${customizationId}`);
      if (response.data.success) {
        const updated = response.data.data;
        setSoldOut(updated.sold_out);
        handleInputChange('sold_out', updated.sold_out);
        setCustomizationData((prev) => ({
          ...prev!,
          sold_out: updated.sold_out,
          updatedAt: updated.updatedAt,
        }));
        notification.success(
          'Stock status updated',
          `Customization is now ${updated.sold_out ? 'sold out' : 'available'}.`,
          3
        );
        window.dispatchEvent(new CustomEvent('customizationUpdated', { detail: { customization: updated } }));
      } else {
        throw new Error('API response indicates failure');
      }
    } catch (error: any) {
      setSoldOut(!soldOutValue);
      handleInputChange('sold_out', !soldOutValue);
      notification.error(
        'Error updating stock status',
        (error as any).response?.data?.message || 'Failed to update stock status. Please try again.',
        5
      );
    }
  };

  const handleRelatedItemsChange = (updatedItems: RelatedItem[]) => {
    setCustomizationData((prevData) => ({
      ...prevData!,
      related_items: updatedItems,
    }));
  };

  return {
    isEditModalOpen,
    isSavingName,
    editOptionsMode,
    isOption,
    addToItemsMode,
    customizationRulesMode,
    soldOut,
    setSoldOut,
    handleEditNameSave,
    openEditModal: () => setIsEditModalOpen(true),
    closeEditModal: () => setIsEditModalOpen(false),
    handleEditOptionsClick: () => setEditOptionsMode(true),
    handleCustomizationRulesClick: () => setCustomizationRulesMode(true),
    handleAddToItemsClick: () => setAddToItemsMode(true),
    handleBackClick,
    handleStockToggle,
    handleRelatedItemsChange,
  };
};
